import { Reveal } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, ArrowBtn, PageHero } from '../components/bits';
import { useLead } from '../components/LeadModal';

const WORK_WITH = ['Government schools', 'Regional colleges', 'Universities', 'Educational institutions'];
const PARTNERSHIPS = ['Post-Class-12 pathways', 'Embedded digital programmes', 'Parallel skilling', 'Credit-bearing electives where appropriate', 'Institutional capability development', 'Flagship Course delivery partnerships'];
const VALUE = ['Practical skill development', 'Industry-oriented learning', 'Student digital assets', 'Project-based outcomes', 'Institutional differentiation', 'Outcome reporting'];

const STACK = [
  { t: 'Certified Course Structure', d: 'A structured curriculum framework for delivering the selected DITEC flagship programme.' },
  { t: 'Trainer Enablement', d: 'Access to trainer guidance, instructional frameworks and role-specific support for faculty or trainers responsible for delivery.' },
  { t: 'Learning & Delivery Intelligence', d: 'Programme documentation, delivery guidance, assessments, project frameworks and supporting implementation intelligence.' },
  { t: 'Digital Asset Frameworks', d: 'Templates and structures that help institutions guide learners in building and maintaining their required digital assets.' },
  { t: 'Assessment & Evaluation Frameworks', d: 'Structured approaches for evaluating practical output, project work, participation, reflection and progression.' },
  { t: 'Industry & Market Context', d: 'Relevant market-oriented context and practitioner-informed guidance to keep the programme connected to real digital requirements.' },
  { t: 'Implementation Support', d: "Institutional guidance for adapting the flagship course structure to the participating institution's operational environment while maintaining the core DITEC framework." },
];

export default function Institutions() {
  const { open } = useLead();
  return (
    <div data-testid="institutions-page">
    <SEO title="Institutional Partnerships" description="Bring DITEC's flagship learning model into your school, college or university." />
      <PageHero
        ck="institutions"
        tag="School, College & Educational Institution Partnerships"
        lines={<>Build a Stronger Digital Pathway for <span className="font-serif italic font-normal text-electric">Your Students.</span></>}
      >
        <ArrowBtn onClick={() => open('Institutional Partnership')} variant="primary" testId="inst-cta-partner">Partner With DITEC</ArrowBtn>
      </PageHero>

      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-3">
          <div>
            <SectionTag>01 — DITEC Can Work With</SectionTag>
            <div className="mt-8 space-y-3">
              {WORK_WITH.map((w) => (
                <Reveal key={w} className="rounded-2xl border border-navy/10 bg-white px-6 py-4 text-sm font-semibold">{w}</Reveal>
              ))}
            </div>
          </div>
          <div>
            <SectionTag>02 — Partnership Possibilities</SectionTag>
            <ul className="mt-8 space-y-3">
              {PARTNERSHIPS.map((p) => (
                <Reveal key={p} className="flex items-center gap-3 text-sm text-navy/75"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-electric" />{p}</Reveal>
              ))}
            </ul>
          </div>
          <div>
            <SectionTag>03 — Institutional Value</SectionTag>
            <ul className="mt-8 space-y-3">
              {VALUE.map((v) => (
                <Reveal key={v} className="flex items-center gap-3 text-sm text-navy/75"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-electric" />{v}</Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-grid-dark bg-navy py-20 text-white sm:py-28">
        <div className="wrap">
          <SectionTag dark>04 — Carry Forward DITEC Flagship Courses</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">Bring DITEC's Flagship Learning Model <span className="font-serif italic font-normal text-gold">Into Your Institution.</span></h2></Reveal>
          <Reveal delay={0.08}>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/70 sm:text-lg">
              Educational institutions that want to carry forward DITEC's flagship courses can work with DITEC through a structured institutional enablement model. DITEC can provide a defined course structure and delivery framework, supported by the knowledge and intelligence assets required to maintain the intended learning experience.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {STACK.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05} className={`rounded-3xl border border-white/10 bg-navy-800/70 p-8 transition-colors duration-500 hover:border-gold/40 ${i === 6 ? 'sm:col-span-2 lg:col-span-3' : ''}`}>
                <span className="font-mono text-xs text-gold/60">0{i + 1}</span>
                <h3 className="mt-3 text-lg font-semibold tracking-tight">{s.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-white/60">{s.d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 rounded-3xl border border-gold/20 bg-navy-800/70 p-8 sm:p-10">
              <h3 className="text-h3 font-semibold">Who Is This For?</h3>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/70 sm:text-base">
                Schools, colleges, universities and educational institutions that want to offer a structured DITEC pathway without having to independently build the entire curriculum, trainer framework and supporting learning infrastructure from scratch.
              </p>
              <div className="mt-7"><ArrowBtn onClick={() => open('Institutional Partnership')} variant="gold" testId="inst-cta-deliver">Partner to Deliver DITEC Flagship Courses</ArrowBtn></div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
